import { Localized } from "@fluent/react";
import { Box, CircularProgress, FormControlLabel, Paper, Switch, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { impossible } from "../../../app/Utils";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { isTestsEnabled as readTestsEnabled, TESTS_ENABLED_SETTING, updateDocumentSetting } from "../documentsApi";
import { isDocEditable } from "../documentSlice";
import { selectDocument } from "../documentSlice";
import { RiskAnalysisLimitsSection } from "../riskAnalysis/sections/RiskAnalysisLimitsSection";

const ISHIKAWA_SETTING_NAME = "ISHIKAWA_DIAGRAM_ENABLED"

const parseBooleanSetting = (value: string | undefined, fallback: boolean) => {
    if (value === undefined) return fallback
    return value.toLowerCase() === "true"
}

export const DocumentSettings = () => {
    const dispatch = useAppDispatch()
    const { header } = useAppSelector(selectDocument)?.doc ?? impossible()
    const editable = useAppSelector(isDocEditable)
    const [savingIshikawa, setSavingIshikawa] = useState(false)
    const [savingTests, setSavingTests] = useState(false)

    const ishikawaSetting = useMemo(
        () => header.settings.find((setting) => setting.name === ISHIKAWA_SETTING_NAME),
        [header.settings]
    )

    const defaultEnabledFromMethod = (header as any).riskAnalysisMethod === "ISHIKAWA"
    const isIshikawaEnabled = parseBooleanSetting(ishikawaSetting?.value, defaultEnabledFromMethod)

    const isTestsEnabled = useMemo(() => readTestsEnabled(header.settings), [header.settings])

    const handleIshikawaToggle = async (enabled: boolean) => {
        try {
            setSavingIshikawa(true)
            await dispatch(updateDocumentSetting({
                documentId: header.id,
                name: ISHIKAWA_SETTING_NAME,
                value: String(enabled),
            })).unwrap()
            dispatch(showSuccess("saved"))
        } catch {
            dispatch(showError("error"))
        } finally {
            setSavingIshikawa(false)
        }
    }

    const handleTestsToggle = async (enabled: boolean) => {
        try {
            setSavingTests(true)
            await dispatch(updateDocumentSetting({
                documentId: header.id,
                name: TESTS_ENABLED_SETTING,
                value: enabled ? "TRUE" : "FALSE",
            })).unwrap()
            dispatch(showSuccess("saved"))
        } catch {
            dispatch(showError("error"))
        } finally {
            setSavingTests(false)
        }
    }

    if (header.type === 'RA') {
        return (
            <>
                <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                        <Typography variant="h6">
                            <Localized id="settings">Ustawienia</Localized>
                        </Typography>
                        {(savingIshikawa || savingTests) && <CircularProgress size={20} />}
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isIshikawaEnabled}
                                    onChange={(event) => handleIshikawaToggle(event.target.checked)}
                                    disabled={!editable || savingIshikawa}
                                    color="primary"
                                />
                            }
                            label={<Localized id="risk-analysis-ishikawa-availability-switch"><span>Diagram Ishikawy</span></Localized>}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isTestsEnabled}
                                    onChange={(event) => handleTestsToggle(event.target.checked)}
                                    disabled={!editable || savingTests}
                                    color="primary"
                                />
                            }
                            label={<Localized id="risk-analysis-tests-enabled-switch"><span>Testy</span></Localized>}
                        />
                    </Box>
                </Paper>
                <RiskAnalysisLimitsSection documentId={Number(header.id)} />
            </>
        )
    }

    return null
}
